import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  UsersThree,
  Flag,
  Plus,
  Archive,
  Star,
  ArrowsClockwise,
  Check,
  X,
} from "@phosphor-icons/react";

const TOPICS = ["healing", "direction", "anxiety", "family", "grief", "new-believers", "men", "women", "young-adults", "parents"];

export default function CirclesAdminPanel() {
  const [circles, setCircles] = useState([]);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ name: "", description: "", topic: "anxiety", capacity: 6, visibility: "public", is_featured: false });

  const load = async () => {
    setLoading(true);
    try {
      const [c, f] = await Promise.all([
        api.get("/admin/circles"),
        api.get("/admin/circles/flags?status=open"),
      ]);
      setCircles(c.data.items || []);
      setFlags(f.data.items || []);
    } catch {
      toast.error("Couldn't load circles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.name.trim() || !draft.description.trim()) { toast.error("Name + description needed"); return; }
    try {
      await api.post("/admin/circles", draft);
      toast.success("Circle created");
      setShowCreate(false);
      setDraft({ name: "", description: "", topic: "anxiety", capacity: 6, visibility: "public", is_featured: false });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create");
    }
  };

  const toggleFeatured = async (c) => {
    try {
      await api.put(`/admin/circles/${c.id}`, { is_featured: !c.is_featured });
      load();
    } catch { toast.error("Update failed"); }
  };

  const archive = async (c) => {
    if (!window.confirm(`Archive "${c.name}"? Members will lose access.`)) return;
    try {
      await api.delete(`/admin/circles/${c.id}`);
      toast("Archived");
      load();
    } catch { toast.error("Archive failed"); }
  };

  const setStatus = async (c, status) => {
    try {
      await api.put(`/admin/circles/${c.id}`, { status });
      load();
    } catch { toast.error("Update failed"); }
  };

  const resolveFlag = async (id, decision) => {
    try {
      await api.post(`/admin/circles/flags/${id}/resolve`, { decision });
      toast.success(`Flag ${decision}`);
      load();
    } catch { toast.error("Couldn't resolve"); }
  };

  return (
    <div className="space-y-5" data-testid="circles-admin-panel">
      {/* Header */}
      <div className="border border-slate-200 rounded-xl bg-white p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <UsersThree size={22} weight="duotone" className="text-[#C4A265]" />
            <div>
              <h3 className="font-heading text-lg text-slate-900">Prayer Circles</h3>
              <p className="text-xs text-slate-500">Small, intentional groups walking together.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="refresh-circles-btn">
              <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={() => setShowCreate((v) => !v)}
              data-testid="new-circle-btn"
              className="px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-medium inline-flex items-center gap-1 hover:bg-slate-800"
            >
              <Plus size={12} /> New circle
            </button>
          </div>
        </div>

        {showCreate && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="new-circle-form">
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Circle name"
              className="px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
              data-testid="circle-name-input"
            />
            <select
              value={draft.topic}
              onChange={(e) => setDraft({ ...draft, topic: e.target.value })}
              className="px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
              data-testid="circle-topic-input"
            >
              {TOPICS.map((t) => <option key={t}>{t}</option>)}
            </select>
            <textarea
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Short description"
              rows={2}
              className="md:col-span-2 px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
              data-testid="circle-desc-input"
            />
            <div className="flex items-center gap-3">
              <label className="text-xs text-slate-600">Capacity
                <input
                  type="number" min={3} max={8}
                  value={draft.capacity}
                  onChange={(e) => setDraft({ ...draft, capacity: Math.max(3, Math.min(8, Number(e.target.value) || 6)) })}
                  className="ml-2 w-16 px-2 py-1 rounded-md border border-slate-200 text-sm bg-white"
                />
              </label>
              <select
                value={draft.visibility}
                onChange={(e) => setDraft({ ...draft, visibility: e.target.value })}
                className="px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
              >
                <option value="public">public</option>
                <option value="invite_only">invite-only</option>
                <option value="private">private</option>
              </select>
            </div>
            <button
              onClick={create}
              data-testid="create-circle-btn"
              className="px-4 py-2 rounded-full bg-[#2D1B0E] text-white text-xs font-medium hover:bg-[#3D2B1F]"
            >
              Create
            </button>
          </div>
        )}
      </div>

      {/* Flag queue */}
      <div className="border border-slate-200 rounded-xl bg-white p-5" data-testid="flag-queue">
        <div className="flex items-center gap-2 mb-3">
          <Flag size={18} weight="duotone" className="text-rose-600" />
          <h4 className="font-heading text-sm text-slate-900">Open flags ({flags.length})</h4>
        </div>
        {flags.length === 0 ? (
          <p className="text-xs text-slate-500">No open flags. Quiet circles.</p>
        ) : (
          <div className="space-y-2">
            {flags.map((f) => (
              <div key={f.id} className="border border-slate-100 rounded-md p-3 text-xs flex items-start justify-between gap-3" data-testid={`flag-${f.id}`}>
                <div>
                  <p className="text-slate-700"><b>{f.scope}</b> — {f.reason}</p>
                  <p className="text-slate-400 mt-0.5">{f.reporter_email} · {new Date(f.created_at).toLocaleString()}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => resolveFlag(f.id, "resolved")} className="px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 inline-flex items-center gap-1 hover:bg-emerald-100" data-testid={`resolve-${f.id}`}>
                    <Check size={11} /> Resolve
                  </button>
                  <button onClick={() => resolveFlag(f.id, "dismissed")} className="px-2 py-1 rounded-md bg-slate-50 text-slate-600 inline-flex items-center gap-1 hover:bg-slate-100" data-testid={`dismiss-${f.id}`}>
                    <X size={11} /> Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Circles table */}
      <div className="border border-slate-200 rounded-xl bg-white p-5">
        <h4 className="font-heading text-sm text-slate-900 mb-3">All circles ({circles.length})</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-500 text-[10px] uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="text-left py-1.5 pr-3">Name</th>
                <th className="text-left py-1.5 pr-3">Topic</th>
                <th className="text-left py-1.5 pr-3">Status</th>
                <th className="text-right py-1.5 pr-3">Members</th>
                <th className="text-right py-1.5 pr-3">Cap</th>
                <th className="text-right py-1.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {circles.map((c) => (
                <tr key={c.id} className="border-t border-slate-100" data-testid={`row-${c.slug}`}>
                  <td className="py-1.5 pr-3">
                    <div className="font-medium text-slate-800 inline-flex items-center gap-1">
                      {c.is_featured && <Star size={11} weight="fill" className="text-amber-500" />}
                      {c.name}
                    </div>
                    <div className="text-[10px] text-slate-400">{c.slug}</div>
                  </td>
                  <td className="py-1.5 pr-3 text-slate-600">{c.topic}</td>
                  <td className="py-1.5 pr-3">
                    <select
                      value={c.status}
                      onChange={(e) => setStatus(c, e.target.value)}
                      className="px-2 py-1 rounded-md border border-slate-200 text-xs bg-white"
                      data-testid={`status-${c.slug}`}
                    >
                      {["open", "full", "active", "closed", "archived"].map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="py-1.5 pr-3 text-right">{c.member_count}</td>
                  <td className="py-1.5 pr-3 text-right text-slate-500">{c.capacity}</td>
                  <td className="py-1.5 text-right">
                    <button onClick={() => toggleFeatured(c)} className="text-amber-600 hover:text-amber-700 mr-2" title="Featured" data-testid={`star-${c.slug}`}>
                      <Star size={13} weight={c.is_featured ? "fill" : "regular"} />
                    </button>
                    <button onClick={() => archive(c)} className="text-rose-700 hover:text-rose-800" title="Archive" data-testid={`archive-${c.slug}`}>
                      <Archive size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
